/**
 * Phase 126 — Academy Manage Surface at /arenax/host/academy/manage.
 *
 * Institution operations command center.  Overview tiles + modular grid
 * linking into existing Out-Play workspaces (Students, Coaches) and
 * ArenaX surfaces (Venue, Squads, Feed).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import OperatorCockpit from '@/components/arenax/OperatorCockpit';
import {
  Building2, ArrowLeft, Loader2, Users, GraduationCap, MapPin, Calendar,
  Shield, Eye, Trophy, TrendingUp, ArrowRight, Sparkles, Newspaper,
} from 'lucide-react';

export default function ArenaxHostAcademyManage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [academyId, setAcademyId] = useState(null);
  const [primaryName, setPrimaryName] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get('/arenax/host/me/primary-entities');
        const aid = r.data?.academy?.id;
        // Phase 178.5.B — capture canonical name immediately so the header
        // paints with the real identity, never the "Your academy" fallback.
        if (r.data?.academy?.name) setPrimaryName(r.data.academy.name);
        if (!aid) { setLoading(false); return; }
        setAcademyId(aid);
        const ovr = await api.get(`/arenax/host/academy/${aid}/overview`);
        setOverview(ovr.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load academy');
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Building2 className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to manage your academy
          </h1>
          <button onClick={() => navigate('/arenax/auth/login')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}>
            Sign in
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  if (loading) {
    return (
      <ArenaxLayout>
        <div className="flex justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        </div>
      </ArenaxLayout>
    );
  }

  if (!academyId) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Building2 className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            You don't have an academy yet
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Register an academy first to unlock the institution command center.
          </p>
          <button onClick={() => navigate('/arenax/host/academy')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                  data-testid="academy-manage-register-cta">
            Register academy
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <ArenaxLayout>
      <section className="max-w-6xl mx-auto px-5 py-8 arenax-themed space-y-8" data-testid="arenax-academy-manage-page">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button onClick={() => navigate('/arenax/host')}
                    className="text-xs font-semibold flex items-center gap-1 mb-2"
                    style={{ color: 'hsl(var(--ax-text-muted))' }}
                    data-testid="academy-manage-back">
              <ArrowLeft className="h-3 w-3" /> Back to hub
            </button>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
                style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}
                data-testid="academy-manage-header-name">
              {overview?.academy_name || primaryName || 'Your academy'}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
              Institution operations · The grounds, the people, the rituals.
            </p>
          </div>
          {overview?.arenax_enabled && (
            <div className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                 style={{ background: 'rgba(16,185,129,0.10)', color: '#047857', border: '1px solid rgba(16,185,129,0.30)' }}>
              <Sparkles className="h-3 w-3" /> Live on ArenaX
            </div>
          )}
        </div>

        {/* Operator Cockpit — Phase 178.6 (replaces flat tile grid) */}
        <OperatorCockpit kind="academy" entityId={academyId} overview={overview} />

        {/* Modules grid */}
        <div>
          <h2 className="text-base font-bold tracking-tight mb-3" style={{ color: 'hsl(var(--ax-text))' }}>
            Modules
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Module
              icon={MapPin} title="Venue & Slots"
              subtitle="Court inventory, weekly schedule, slot calendar"
              testId="academy-manage-module-venue"
              onClick={() => navigate(
                overview?.venue_id
                  ? `/arenax/host/venue/manage?venue_id=${overview.venue_id}`
                  : '/arenax/host/venue'
              )}
            />
            <Module
              icon={GraduationCap} title="Students"
              subtitle="Enrollment, attendance, performance"
              testId="academy-manage-module-students"
              href={`${backendUrl}/students`}
              external
            />
            <Module
              icon={Users} title="Coaches"
              subtitle="Staff, payroll, schedules"
              testId="academy-manage-module-coaches"
              href={`${backendUrl}/employees`}
              external
            />
            <Module
              icon={Calendar} title="Bookings"
              subtitle="Slot requests, confirmations"
              testId="academy-manage-module-bookings"
              onClick={() => navigate(
                overview?.venue_id
                  ? `/arenax/host/venue/manage?venue_id=${overview.venue_id}`
                  : '/arenax/host/venue'
              )}
            />
            <Module
              icon={Trophy} title="Squads"
              subtitle="Academy-affiliated teams"
              testId="academy-manage-module-squads"
              onClick={() => navigate('/arenax/squads')}
            />
            <Module
              icon={Newspaper} title="Academy feed"
              subtitle="Updates, results, announcements"
              testId="academy-manage-module-feed"
              onClick={() => navigate('/arenax')}
            />
            <Module
              icon={TrendingUp} title="Revenue"
              subtitle="Slot revenue, subscription income"
              testId="academy-manage-module-revenue"
              onClick={() => navigate(
                overview?.venue_id
                  ? `/arenax/host/venue/manage?venue_id=${overview.venue_id}`
                  : '/arenax/host/venue'
              )}
            />
            <Module
              icon={Eye} title="ArenaX visibility"
              subtitle="How players discover you"
              testId="academy-manage-module-visibility"
              onClick={() => navigate('/arenax/venues')}
            />
            <Module
              icon={Shield} title="Verification"
              subtitle="Become a verified academy"
              testId="academy-manage-module-verification"
              comingSoon
            />
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Tile({ icon: Icon, label, value, hint }) {
  return (
    <div className="arenax-card p-4 rounded-xl" data-testid={`academy-tile-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: 'hsl(var(--ax-text-faint))' }}>{label}</span>
        <Icon className="h-3.5 w-3.5" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
      </div>
      <div className="text-xl font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
        {value}
      </div>
      {hint && <div className="text-[10px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>{hint}</div>}
    </div>
  );
}

function Module({ icon: Icon, title, subtitle, onClick, href, external, comingSoon, testId }) {
  const content = (
    <>
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-4 w-4" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
        {comingSoon && (
          <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'var(--ax-card-bg-soft)', color: 'hsl(var(--ax-text-faint))' }}>
            Soon
          </span>
        )}
        {!comingSoon && <ArrowRight className="h-3.5 w-3.5" style={{ color: 'hsl(var(--ax-text-faint))' }} />}
      </div>
      <div className="text-sm font-bold mb-0.5" style={{ color: 'hsl(var(--ax-text))' }}>{title}</div>
      <div className="text-[11px]" style={{ color: 'hsl(var(--ax-text-muted))' }}>{subtitle}</div>
    </>
  );
  const className = "arenax-card text-left p-4 rounded-xl transition-all hover:-translate-y-0.5 cursor-pointer";
  if (comingSoon) {
    return (
      <div className={className + ' opacity-60 cursor-default'} data-testid={testId}>
        {content}
      </div>
    );
  }
  if (href) {
    return (
      <a href={href} target={external ? '_blank' : '_self'} rel="noreferrer"
         className={className} data-testid={testId}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className + ' w-full block'} data-testid={testId}>
      {content}
    </button>
  );
}
